import { Box2, DataTexture, RGBAFormat, type Texture, UnsignedByteType, Vector2 } from "three/webgpu";
import type { GpuContext } from "../../engine/GpuContext.js";
import { configureTexture } from "../../engine/texture.js";

export interface FontSpec {
  family: string;
  /** Logical pixels. */
  size: number;
  weight?: number | string;
  italic?: boolean;
}

export interface Glyph {
  /** Index into the atlas page list; -1 for a blank glyph (e.g. space). */
  page: number;
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** Bitmap size in device pixels. */
  width: number;
  height: number;
  /** Offset from the pen position to the bitmap's left edge, device px. */
  bearingX: number;
  /** Distance from the baseline up to the bitmap's top edge, device px. */
  bearingY: number;
  /** Pen advance, device px. */
  advance: number;
}

export interface FontMetrics {
  /** All in device pixels, for the raster size they were measured at. */
  ascent: number;
  descent: number;
  lineHeight: number;
}

/**
 * One font at one raster size: its metrics and every glyph drawn in it.
 *
 * Resolved once per string rather than once per character. Looking a glyph up
 * by a `family|weight|italic|px|char` key built two template strings and hashed
 * a fresh one for every glyph on screen, every frame; a face turns that into a
 * property read and an array index for ASCII.
 */
export interface GlyphFace {
  readonly rasterPx: number;
  readonly css: string;
  readonly metrics: FontMetrics;
  /** Glyphs for code points below 128, by code point. */
  readonly ascii: (Glyph | undefined)[];
  readonly glyphs: Map<number, Glyph>;
}

/** A face as reached through one spec, with the fields it had when it got there. */
interface FaceEntry {
  face: GlyphFace;
  family: string;
  weight: FontSpec["weight"];
  italic: FontSpec["italic"];
}

const PAGE_SIZE = 2048;
/** Transparent gap around each glyph so bilinear sampling cannot bleed. */
const PAD = 1;

interface Page {
  texture: DataTexture;
  /** The page's pixels on this side, so a new glyph is uploaded as a rectangle rather than the page. */
  pixels: Uint8Array;
  /** Rows and columns written since the last upload: x0, y0, x1, y1, or x1 < x0 when clean. */
  dirty: [number, number, number, number];
  /** Shelf packer cursor. */
  x: number;
  y: number;
  shelfHeight: number;
}

const BLANK: Glyph = {
  page: -1,
  u0: 0,
  v0: 0,
  u1: 0,
  v1: 0,
  width: 0,
  height: 0,
  bearingX: 0,
  bearingY: 0,
  advance: 0,
};

/**
 * Rasterizes glyphs with the browser's own text engine (OffscreenCanvas 2D)
 * into shelf-packed GPU atlas pages. New glyphs are gathered over a frame and
 * uploaded together by `upload`, as one rectangle a page.
 *
 * Glyphs are keyed by device-pixel raster size, so the same logical font at
 * DPR 1.75 or inside a 2x-zoomed ScrollViewer gets its own crisp raster rather
 * than a bilinear-scaled one.
 */
export class GlyphAtlas {
  #gpu: GpuContext;
  #pages: Page[] = [];
  /** By `fontKey`: the fallback for a spec that is built afresh per call. */
  #faces = new Map<string, GlyphFace>();
  /** By spec identity, then raster size: the fast path for a theme's shared fonts. */
  #byFont = new WeakMap<FontSpec, Map<number, FaceEntry>>();
  #glyphCount = 0;
  #scratch: OffscreenCanvas;
  #ctx: OffscreenCanvasRenderingContext2D;
  #measureCtx: OffscreenCanvasRenderingContext2D;
  /** Last `font` assigned to each context: assigning one re-parses the CSS even when unchanged. */
  #ctxFont = "";
  #measureFont = "";

  constructor(gpu: GpuContext) {
    this.#gpu = gpu;
    // Sized for the largest glyph we expect; grown on demand.
    this.#scratch = new OffscreenCanvas(256, 256);
    const ctx = this.#scratch.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("webwin: OffscreenCanvas 2D unavailable for glyph rasterization.");
    this.#ctx = ctx;
    const mctx = new OffscreenCanvas(8, 8).getContext("2d");
    if (!mctx) throw new Error("webwin: OffscreenCanvas 2D unavailable for text measurement.");
    this.#measureCtx = mctx;
  }

  get pageCount(): number {
    return this.#pages.length;
  }

  get glyphCount(): number {
    return this.#glyphCount;
  }

  texture(page: number): Texture {
    const p = this.#pages[page];
    if (!p) throw new Error(`webwin: no glyph atlas page ${page}`);
    return p.texture;
  }

  /**
   * Send every glyph rasterized since the last call to the GPU. Called once a frame, before the frame's
   * quads are drawn: the rows a page gained are copied up as one rectangle, rather than a texture write per
   * glyph or the whole page every time.
   */
  upload(): void {
    for (const page of this.#pages) {
      const [x0, y0, x1, y1] = page.dirty;
      if (x1 <= x0 || y1 <= y0) continue;
      page.dirty = [PAGE_SIZE, PAGE_SIZE, 0, 0];
      if (page.texture.version === 0) {
        // Never uploaded: the first use sends the whole page, glyphs included.
        page.texture.needsUpdate = true;
        continue;
      }
      const w = x1 - x0;
      const h = y1 - y0;
      const rect = new Uint8Array(w * h * 4);
      for (let y = 0; y < h; y++) {
        const from = ((y0 + y) * PAGE_SIZE + x0) * 4;
        rect.set(page.pixels.subarray(from, from + w * 4), y * w * 4);
      }
      const staging = configureTexture(new DataTexture(rect, w, h, RGBAFormat, UnsignedByteType), false);
      staging.needsUpdate = true;
      this.#gpu.renderer.copyTextureToTexture(staging, page.texture, new Box2(new Vector2(0, 0), new Vector2(w, h)), new Vector2(x0, y0));
      staging.dispose();
    }
  }

  /** CSS font shorthand for a spec at a given device-pixel raster size. */
  static cssFont(font: FontSpec, rasterPx: number): string {
    const style = font.italic ? "italic " : "";
    const weight = font.weight ?? 400;
    return `${style}${weight} ${rasterPx}px ${font.family}`;
  }

  static fontKey(font: FontSpec, rasterPx: number): string {
    return `${font.family}|${font.weight ?? 400}|${font.italic ? 1 : 0}|${rasterPx}`;
  }

  /**
   * The face for `font` at `rasterPx`.
   *
   * Found by the spec's identity first. A spec is a plain object an app is free
   * to mutate, so a hit is checked against the fields that name the typeface
   * before it is trusted; a miss falls back to the string key.
   */
  face(font: FontSpec, rasterPx: number): GlyphFace {
    let sizes = this.#byFont.get(font);
    const hit = sizes?.get(rasterPx);
    if (
      hit &&
      hit.family === font.family &&
      hit.weight === font.weight &&
      hit.italic === font.italic
    ) {
      return hit.face;
    }
    const key = GlyphAtlas.fontKey(font, rasterPx);
    let face = this.#faces.get(key);
    if (!face) {
      face = {
        rasterPx,
        css: GlyphAtlas.cssFont(font, rasterPx),
        metrics: this.#measureMetrics(font, rasterPx),
        ascii: [],
        glyphs: new Map(),
      };
      this.#faces.set(key, face);
    }
    if (!sizes) this.#byFont.set(font, (sizes = new Map()));
    sizes.set(rasterPx, { face, family: font.family, weight: font.weight, italic: font.italic });
    return face;
  }

  metrics(font: FontSpec, rasterPx: number): FontMetrics {
    return this.face(font, rasterPx).metrics;
  }

  #measureMetrics(font: FontSpec, rasterPx: number): FontMetrics {
    const probe = this.#measure(GlyphAtlas.cssFont(font, rasterPx), "Hxg");
    const ascent = probe.fontBoundingBoxAscent || probe.actualBoundingBoxAscent || rasterPx * 0.8;
    const descent =
      probe.fontBoundingBoxDescent || probe.actualBoundingBoxDescent || rasterPx * 0.2;
    return { ascent, descent, lineHeight: Math.ceil(ascent + descent) };
  }

  #measure(css: string, text: string): globalThis.TextMetrics {
    const ctx = this.#measureCtx;
    if (this.#measureFont !== css) {
      ctx.font = css;
      this.#measureFont = css;
    }
    ctx.textBaseline = "alphabetic";
    return ctx.measureText(text);
  }

  /** Pen advance for one character, device px. Cheaper than a full raster. */
  advance(font: FontSpec, rasterPx: number, ch: string): number {
    return this.glyph(font, rasterPx, ch).advance;
  }

  glyph(font: FontSpec, rasterPx: number, ch: string): Glyph {
    return this.glyphOf(this.face(font, rasterPx), ch.codePointAt(0) ?? 0);
  }

  /** The glyph for one code point in `face`, rasterizing it on first use. */
  glyphOf(face: GlyphFace, code: number): Glyph {
    if (code < 128) {
      const cached = face.ascii[code];
      if (cached) return cached;
    } else {
      const cached = face.glyphs.get(code);
      if (cached) return cached;
    }
    const glyph = this.#rasterize(face, String.fromCodePoint(code));
    if (code < 128) (face.ascii as Glyph[])[code] = glyph;
    else face.glyphs.set(code, glyph);
    this.#glyphCount++;
    return glyph;
  }

  #rasterize(face: GlyphFace, ch: string): Glyph {
    const css = face.css;
    const m = this.#measure(css, ch);
    const advance = m.width;

    // Ink extents relative to the pen origin, snapped outward to whole pixels.
    const left = Math.ceil(Math.max(0, m.actualBoundingBoxLeft));
    const right = Math.ceil(Math.max(0, m.actualBoundingBoxRight));
    const ascent = Math.ceil(Math.max(0, m.actualBoundingBoxAscent));
    const descent = Math.ceil(Math.max(0, m.actualBoundingBoxDescent));
    const inkW = left + right;
    const inkH = ascent + descent;
    if (inkW <= 0 || inkH <= 0) return { ...BLANK, advance };

    const w = inkW + PAD * 2;
    const h = inkH + PAD * 2;
    if (w > PAGE_SIZE || h > PAGE_SIZE) return { ...BLANK, advance };

    // Rasterize white-on-transparent; the shader inks it with the instance color.
    if (this.#scratch.width < w || this.#scratch.height < h) {
      this.#scratch.width = Math.max(this.#scratch.width, w);
      this.#scratch.height = Math.max(this.#scratch.height, h);
      // Resizing a canvas resets its context, font included.
      this.#ctxFont = "";
    }
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, w, h);
    if (this.#ctxFont !== css) {
      ctx.font = css;
      this.#ctxFont = css;
    }
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(ch, left + PAD, ascent + PAD);

    const pixels = ctx.getImageData(0, 0, w, h);
    const slot = this.#allocate(w, h);
    const page = this.#pages[slot.page]!;
    for (let y = 0; y < h; y++) {
      page.pixels.set(pixels.data.subarray(y * w * 4, (y + 1) * w * 4), ((slot.y + y) * PAGE_SIZE + slot.x) * 4);
    }
    const d = page.dirty;
    d[0] = Math.min(d[0], slot.x);
    d[1] = Math.min(d[1], slot.y);
    d[2] = Math.max(d[2], slot.x + w);
    d[3] = Math.max(d[3], slot.y + h);

    return {
      page: slot.page,
      u0: slot.x / PAGE_SIZE,
      v0: slot.y / PAGE_SIZE,
      u1: (slot.x + w) / PAGE_SIZE,
      v1: (slot.y + h) / PAGE_SIZE,
      width: w,
      height: h,
      bearingX: -(left + PAD),
      bearingY: ascent + PAD,
      advance,
    };
  }

  #allocate(w: number, h: number): { page: number; x: number; y: number } {
    for (let i = 0; i < this.#pages.length; i++) {
      const p = this.#pages[i]!;
      if (p.x + w <= PAGE_SIZE && p.y + h <= PAGE_SIZE) {
        const slot = { page: i, x: p.x, y: p.y };
        p.x += w;
        p.shelfHeight = Math.max(p.shelfHeight, h);
        return slot;
      }
      // Start a new shelf on this page.
      if (p.y + p.shelfHeight + h <= PAGE_SIZE) {
        p.y += p.shelfHeight;
        p.x = w;
        p.shelfHeight = h;
        return { page: i, x: 0, y: p.y };
      }
    }
    this.#addPage();
    return this.#allocate(w, h);
  }

  #addPage(): void {
    const pixels = new Uint8Array(PAGE_SIZE * PAGE_SIZE * 4);
    const texture = configureTexture(new DataTexture(pixels, PAGE_SIZE, PAGE_SIZE, RGBAFormat, UnsignedByteType));
    texture.name = `webwin.glyphAtlas${this.#pages.length}`;
    this.#pages.push({
      texture,
      pixels,
      dirty: [PAGE_SIZE, PAGE_SIZE, 0, 0],
      x: 0,
      y: 0,
      shelfHeight: 0,
    });
  }

  /** Drop every cached glyph and page — used after a device loss. */
  clear(): void {
    for (const p of this.#pages) p.texture.dispose();
    this.#pages.length = 0;
    this.#faces.clear();
    this.#byFont = new WeakMap();
    this.#glyphCount = 0;
  }

  dispose(): void {
    this.clear();
  }
}
